import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import { toast } from "react-toastify";
import {} from "../../../../../../api/Vendor/flight";
import {
  cardStyles,
  formInputGroup,
  formSections,
  modalGenericStyles,
  variables,
} from "../../../../../../variables";
import PropTypes from "prop-types";
import _ from "lodash";
import Loader from "../../../../../../components/Loader";
import * as yup from "yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { genericRequestErrorHandler } from "../../../../../../api";
import {
  createNewACTVendors,
  patchACTVendors,
  retrieveACTVendors,
} from "../../../../../../api/Vendor/Activitiy";
import {
  convertStringToBoolean,
  convertStringToNumber,
  currencies,
  handleValidationErrors,
} from "../../../../../../helpers";
import EntityAutocomplete from "../../../../../../components/EntityAutocomplete";
import { CustomButton } from "../../../../../../components/CustomButton";

const titleMapping = {
  "": "-----",
  mr: "Mr",
  ms: "Ms",
};

const trueFalseMapping = {
  false: "No",
  true: "Yes",
};

const environmentMapping = {
  PRODUCTION: "Production",
  STAGING: "Staging",
};

const marketTypeMapping = {
  "": "-----",
  B2B: "B2B",
  B2C: "B2C",
};

const vendorsMapping = {
  "": "-----",
  act__Musement: "Musement",
};
const statusMapping = {
  "": "-----",
  AC: "Active",
  IN: "Inactive",
  CA: "Candidate",
};
const initialForm = {
  vendor: "",
  status: "",
  member: "",
  config_uid: "",
  notify_amount: 0,
  market_type: "",
  is_own: false,
  travel_service_type: "ACT",
  vendor_data: {
    name: "",
    environment: "",
    base_url: "",
    source_entity: "",
    client_id: "",
    client_secret: "",
    is_global: false,
    grand_type: "",
    x_musement_application: "",
    currency: "",
  },
};

const getSchema = ({ required_message, vendor }) => {
  return yup.object().shape({
    vendor: yup.string().required(`Vendor: ${required_message}`),
    status: yup.string().required(`Status: ${required_message}`),
    is_own: yup.boolean(),
    notify_amount: yup.number().nullable(),
    travel_service_type: yup.string().default("ACT"),
    member: yup
      .number()
      .integer()
      .typeError("Member must be an integer")
      .required(`Member: ${required_message}`),
    vendor_data: yup.object().shape({
      base_url: yup.string().test({
        name: "base_url_is_required_for_Musement",
        message: `Base Url: ${required_message}`,
        test: function (value) {
          if (value && value.trim().length === 0) {
            return false;
          }
          return ["act__Musement"].includes(vendor) ? !!value : true;
        },
      }),
      client_id: yup.string().test({
        name: "client_id_is_required_for_Musement",
        message: `Client Id: ${required_message}`,
        test: function (value) {
          if (value && value.trim().length === 0) {
            return false;
          }
          return ["act__Musement"].includes(vendor) ? !!value : true;
        },
      }),
      grand_type: yup.string().test({
        name: "grand_type_is_required_for_Musement",
        message: `Grand Type: ${required_message}`,
        test: function (value) {
          if (value && value.trim().length === 0) {
            return false;
          }
          return ["act__Musement"].includes(vendor) ? !!value : true;
        },
      }),
      x_musement_application: yup.string().test({
        name: "client_id_is_required_for_Musement",
        message: `X Musement Application: ${required_message}`,
        test: function (value) {
          if (value && value.trim().length === 0) {
            return false;
          }
          return ["act__Musement"].includes(vendor) ? !!value : true;
        },
      }),

      currency: yup.string().required(`Currency: ${required_message}`),
      config_uid: yup.array().of(yup.string()),
    }),
  });
};

const styles = createUseStyles({
  ...modalGenericStyles,
  ...cardStyles,
  EditVendorACTModal: {
    ...modalGenericStyles.modal,
  },
  modalCard: {
    ...modalGenericStyles.card,
    gridTemplateRows: "max-content  1fr max-content",
  },
  modalCardHeader: {
    ...modalGenericStyles.cardHeader,
  },
  modalCardBody: {
    ...modalGenericStyles.cardBody,
    display: "grid",
  },
  modalCardActions: {
    ...modalGenericStyles.cardActions,
  },
  card: cardStyles.card,
  cardHeader: cardStyles.header,
  cardBody: {
    ...cardStyles.body,
    gap: variables.normal_gap,
  },
  inputGroup: { ...formInputGroup },
  formSectionHeader: formSections.sectionHeader,
  entityAutoComplete: {
    ...formInputGroup,
    "& select, input": {
      boxSizing: "border-box",
      height: `calc(${variables.normal_gap} * 2.6)`,
      width: "100%",
    },
  },
  cardActions: { ...cardStyles.actions, gridColumn: "2 span" },
  [`@media ${variables.media.smallscreen}`]: {
    modalCard: {
      width: "60rem",
    },
    cardCol2: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    cardCol3: {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
});

const EditVendorACTModal = ({ id, onClose }) => {
  const classes = styles();
  const queryClient = useQueryClient();

  const mode = id ? "edit" : "add";

  const [form, setForm] = useState(initialForm);
  const [autocompleteValue, setAutocompleteValue] = useState("");

  const { isLoading: isFetchingLoading } = useQuery({
    queryKey: ["EditVendorACTModal"],
    queryFn: () => retrieveACTVendors({ id: id }),
    enabled: mode === "edit",
    onSuccess: (data) => {
      setForm(_.get(data, "data"));
      setAutocompleteValue(_.get(data, "data.member"));
    },
    onError: (error) => {
      genericRequestErrorHandler(error);
    },
  });

  const mutation = useMutation({
    mutationFn: async ({ id, payload }) => {
      await validationSchema.validate(form);
      if (mode === "add") {
        return createNewACTVendors({ payload: { ...payload } });
      } else {
        return patchACTVendors({ id, payload: { ...payload } });
      }
    },

    onError: (error) => {
      genericRequestErrorHandler(error);
    },
    onSuccess: () => {
      toast.success(
        mode === "add" ? "New Activity added" : "Vendor Activity info updated"
      );
      queryClient.invalidateQueries("ActivitiesVendors");
      onClose();
    },
  });

  const validationSchema = getSchema({
    required_message: "This field is required.",
    vendor: form.vendor,
  });

  const boolFields = convertStringToBoolean({
    is_global: _.get(form, "vendor_data.is_global"),
    is_own: _.get(form, "is_own"),
  });

  const numericFields = ["notify_amount"];
  const updatedNumericFields = convertStringToNumber(form, numericFields);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await validationSchema.validate(form, { abortEarly: false });

      mutation.mutate({
        id: id,
        payload: {
          ...form,
          ...updatedNumericFields,
          config_uid: _.isEmpty(_.get(form, "config_uid.[0]", []))
            ? []
            : [_.get(form, "config_uid[0]", [])],
          member: _.get(form, "member"),
          vendor_data: {
            ...form.vendor_data,
            ...boolFields,
          },
        },
      });
    } catch (error) {
      handleValidationErrors(error);
    }
  };

  const handleInputChange = (e) => {
    // setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setForm((p) => ({
      ...p,
      [e.target.name]:
        e.target.name === "config_uid" ? [e.target.value] : e.target.value,
    }));
  };
  const handleVendorDataInputChange = (e) => {
    setForm((p) => ({
      ...p,
      vendor_data: {
        ..._.get(form, "vendor_data", {}),
        [e.target.name]: e.target.value,
      },
    }));
  };

  const handleInputContactPersonChange = (e) => {
    setForm((p) => ({
      ...p,
      vendor_data: {
        ..._.get(form, "vendor_data", {}),
        contact_details: {
          ..._.get(form, "vendor_data.contact_details", {}),
          [e.target.name]: e.target.value,
        },
      },
    }));
  };

  const loading = mutation.isLoading || (isFetchingLoading && mode === "edit");

  return (
    <div id="AddEditACTVendorModal" className={classes.EditVendorACTModal}>
      {loading ? (
        <div className={classes.modalCard}>
          <div className={classes.modalCardHeader}>
            <span>{_.startCase(mode)} Activity Vendor</span>
          </div>
          <div className={classes.modalCardBody}>
            <Loader />
          </div>
        </div>
      ) : (
        <div className={classes.modalCard}>
          <div className={classes.modalCardHeader}>
            <span>{_.startCase(mode)} Activity Vendor</span>
          </div>
          <div className={classes.modalCardBody}>
            <form>
              <div className={classes.card}>
                <div className={classes.cardHeader}>Basic Data</div>
                <div className={classes.cardBody}>
                  <div className={classes.cardCol2}>
                    <div className={classes.inputGroup}>
                      <label>Vendor</label>
                      <select
                        id="vendor"
                        name="vendor"
                        value={form.vendor}
                        disabled={mode === "edit"}
                        onChange={handleInputChange}>
                        {Object.entries(vendorsMapping).map(
                          ([key, label], idx) => (
                            <option key={idx} value={key}>
                              {label}
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    <div className={classes.inputGroup}>
                      <label htmlFor="member">Member</label>
                      <div className={classes.entityAutoComplete}>
                        <EntityAutocomplete
                          id="memberAutocomplete"
                          subagents={false}
                          independentAgents={false}
                          branches={false}
                          value={autocompleteValue}
                          disabled={mode === "edit"}
                          setValue={setAutocompleteValue}
                          onChange={(text) => {
                            if (text.length === 0) {
                              setForm((p) => ({
                                ...p,
                                member: "",
                              }));
                              return;
                            }
                            const entityInfos = text.split("---");
                            const entityType = entityInfos[0];
                            const entityId = entityInfos[1];
                            setForm((p) => ({
                              ...p,
                              member: `${entityId}`,
                            }));
                          }}
                        />
                      </div>
                    </div>
                    {/* </div>
                  <div className={classes.cardCol3}> */}
                    <div className={classes.inputGroup}>
                      <label htmlFor="status">Status</label>
                      <select
                        id="status"
                        name="status"
                        value={form.status}
                        onChange={handleInputChange}>
                        {Object.entries(statusMapping).map(
                          ([value, label], idx) => (
                            <option key={idx} value={value}>
                              {label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className={classes.inputGroup}>
                      <label htmlFor="market_type">Market Type</label>
                      <select
                        id="market_type"
                        name="market_type"
                        value={_.get(form, "market_type")}
                        onChange={handleInputChange}>
                        {Object.entries(marketTypeMapping).map(
                          ([value, label], idx) => (
                            <option key={idx} value={value}>
                              {label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Currency</label>
                      <select
                        id="currency"
                        name="currency"
                        value={_.get(form, "vendor_data.currency")}
                        onChange={handleVendorDataInputChange}>
                        {Object.entries(currencies).map(([key, label], idx) => (
                          <option key={idx} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Notify Amount</label>
                      <input
                        type="number"
                        name="notify_amount"
                        id="notify_amount"
                        value={_.get(form, "notify_amount")}
                        onChange={handleInputChange}
                      />
                    </div>
                    {/* </div> */}
                    {/* <div className={classes.cardCol2}> */}
                    <div className={classes.inputGroup}>
                      <label>Config uid</label>
                      <input
                        id="config_uid"
                        type="text"
                        name="config_uid"
                        value={_.get(form, "config_uid[0]")}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label htmlFor="is_own">Is Own</label>
                      <select
                        id="is_own"
                        name="is_own"
                        value={_.get(form, "is_own")}
                        onChange={handleInputChange}>
                        {Object.entries(trueFalseMapping).map(
                          ([value, label], idx) => (
                            <option key={idx} value={value}>
                              {label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.card}>
                <div className={classes.cardHeader}>Vendor Data</div>
                <div className={classes.cardBody}>
                  <div className={classes.cardCol2}>
                    <div className={classes.inputGroup}>
                      <label>Name</label>
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={_.get(form, "vendor_data.name")}
                        onChange={handleVendorDataInputChange}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label htmlFor="source_entity">Source Entity</label>
                      <input
                        id="source_entity"
                        type="text"
                        name="source_entity"
                        disabled
                        value={`${_.get(form, "member")}___member`}
                        onChange={handleVendorDataInputChange}
                      />
                    </div>
                  </div>
                  <div className={classes.cardCol3}>
                    <div className={classes.inputGroup}>
                      <label htmlFor="environment">Environment</label>
                      <select
                        id="environment"
                        name="environment"
                        value={_.get(form, "vendor_data.environment")}
                        onChange={handleVendorDataInputChange}>
                        {Object.entries(environmentMapping).map(
                          ([value, label], idx) => (
                            <option key={idx} value={value}>
                              {label}
                            </option>
                          )
                        )}
                      </select>
                    </div>

                    <div className={classes.inputGroup}>
                      <label>Base url</label>
                      <input
                        id="base_url"
                        type="text"
                        name="base_url"
                        value={_.get(form, "vendor_data.base_url")}
                        onChange={handleVendorDataInputChange}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Grand Type</label>
                      <input
                        id="grand_type"
                        type="text"
                        name="grand_type"
                        value={_.get(form, "vendor_data.grand_type")}
                        onChange={handleVendorDataInputChange}
                      />
                    </div>
                  </div>
                  <div className={classes.cardCol2}>
                    <div className={classes.inputGroup}>
                      <label>X Musement Application</label>
                      <input
                        id="x_musement_application"
                        type="text"
                        name="x_musement_application"
                        value={_.get(
                          form,
                          "vendor_data.x_musement_application"
                        )}
                        onChange={handleVendorDataInputChange}
                      />
                    </div>

                    <div className={classes.inputGroup}>
                      <label htmlFor="is_global">Is Global</label>
                      <select
                        id="is_global"
                        name="is_global"
                        value={_.get(form, "vendor_data.is_global")}
                        onChange={handleVendorDataInputChange}>
                        {Object.entries(trueFalseMapping).map(
                          ([value, label], idx) => (
                            <option key={idx} value={value}>
                              {label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.card}>
                <div className={classes.cardHeader}>Access Data</div>
                <div className={classes.cardBody}>
                  <div className={classes.cardCol2}>
                    <div className={classes.inputGroup}>
                      <label>Client Id</label>
                      <input
                        id="client_id"
                        type="text"
                        name="client_id"
                        value={_.get(form, "vendor_data.client_id")}
                        onChange={handleVendorDataInputChange}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Client Secret</label>
                      <input
                        id="client_secret"
                        type="text"
                        name="client_secret"
                        value={_.get(form, "vendor_data.client_secret")}
                        onChange={handleVendorDataInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.card}>
                <div className={classes.cardHeader}>Contact Person</div>
                <div className={classes.cardBody}>
                  <div className={classes.cardCol2}>
                    <div className={classes.inputGroup}>
                      <label>Gender Title</label>
                      <select
                        id="title"
                        name="title"
                        value={_.get(form, "vendor_data.contact_details.title")}
                        onChange={handleInputContactPersonChange}>
                        {Object.entries(titleMapping).map(
                          ([key, label], idx) => (
                            <option key={idx} value={key}>
                              {label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div className={classes.inputGroup}>
                      <label>First Name</label>
                      <input
                        id="first_name"
                        type="text"
                        name="first_name"
                        value={_.get(
                          form,
                          "vendor_data.contact_details.first_name"
                        )}
                        onChange={handleInputContactPersonChange}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Last Name</label>
                      <input
                        id="last_name"
                        type="text"
                        name="last_name"
                        value={_.get(
                          form,
                          "vendor_data.contact_details.last_name"
                        )}
                        onChange={handleInputContactPersonChange}
                      />
                    </div>
                    <div className={classes.inputGroup}>
                      <label>Email</label>
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={_.get(form, "vendor_data.contact_details.email")}
                        onChange={handleInputContactPersonChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div className={classes.modalCardActions}>
            <CustomButton id="cancel" appearance="ghost" onClick={onClose}>
              <strong>Cancel</strong>
            </CustomButton>
            <CustomButton id="submit" appearance="primary" onClick={onSubmit}>
              <strong>Submit</strong>
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};
EditVendorACTModal.propTypes = {
  id: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default EditVendorACTModal;
